import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus, Globe, Plane, Landmark, Mountain, Compass, Music2,
  Award, BookOpen, Star, Utensils, Briefcase, Hash, Moon,
  Send, Lock, Gift, Pencil, Share2, Users, Zap, Binoculars,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SectionRibbon } from "@/components/AppShell";
import TierProgressBar from "@/components/TierProgressBar";
import RewardsCreditBlock from "@/components/RewardsCreditBlock";
import { PASSPORT } from "@/constants/testIds";
import { toast } from "sonner";

const STAMP_ICONS = {
  torii: Landmark, mountain: Mountain, monument: Landmark, music: Music2,
  statue: Award, boat: Compass, compass: Compass, plane: Plane,
};

const BADGE_ICONS = {
  globe: Globe, plane: Plane, moon: Moon, book: BookOpen,
  utensils: Utensils, star: Star, luggage: Briefcase, hash: Hash,
};

const EARN_ICONS = {
  plane: Plane, pencil: Pencil, share: Share2, users: Users,
};

/* ---------- Stamps ---------- */
function monthYear(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleString("en-US", { month: "short", year: "numeric" }).toUpperCase();
}

const TINTS = {
  "#d65d4f": "#fce4e0", "#1a3a5f": "#e3eaf3", "#7cc4d6": "#e3f1f5",
  "#a695c7": "#ece6f5", "#e69a52": "#fae6d3", "#e6b84a": "#fbf4e0",
  "#8db48e": "#e3eee4", "#c89968": "#f3e5d4",
};

function StampOval({ stamp, idx }) {
  const Icon = STAMP_ICONS[stamp.icon] || Compass;
  const color = stamp.color || "#1a3a5f";
  const tint = TINTS[color] || "#fdf6e7";
  return (
    <motion.div
      data-testid={PASSPORT.stamp(stamp.code || stamp.city)}
      initial={{ scale: 0.6, opacity: 0, rotate: -8 }}
      animate={{ scale: 1, opacity: 1, rotate: idx % 2 ? 2.5 : -2.5 }}
      transition={{ delay: idx * 0.06, type: "spring", stiffness: 220, damping: 16 }}
      className="flex flex-col items-center gap-2"
    >
      <div className="w-28 h-24 rounded-[100%] flex flex-col items-center justify-center"
        style={{ border: `2.5px dashed ${color}`, background: tint }}>
        <Icon className="w-7 h-7 mb-0.5" style={{ color }} strokeWidth={1.7} />
        <div className="font-serif-it text-base leading-none" style={{ color }}>{stamp.city}</div>
        <div className="text-[10px] font-hand mt-0.5" style={{ color }}>{monthYear(stamp.date)}</div>
      </div>
      <div className="text-[10px] font-ui tracking-[0.25em] text-tb-navy/60 uppercase">{stamp.country}</div>
    </motion.div>
  );
}

function NextTripPlaceholder() {
  return (
    <div className="flex flex-col items-center gap-2 opacity-60">
      <div className="w-28 h-24 rounded-[100%] border-2 border-dashed border-tb-navy/30 bg-tb-cream-2/40 flex flex-col items-center justify-center">
        <Plus className="w-6 h-6 text-tb-navy/40" />
        <span className="font-hand text-tb-navy/50 text-sm mt-1">next trip</span>
      </div>
    </div>
  );
}

/* ---------- Achievements (8 badges, grid + in-progress) ---------- */
function AchievementCard({ a }) {
  const Icon = BADGE_ICONS[a.icon] || Award;
  const tier = a.tier;
  const color = a.color;
  const tint = TINTS[color] || "#fdf6e7";
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
  if (a.unlocked) {
    return (
      <div data-testid={PASSPORT.achievement(a.id)}
        className="rounded-2xl border-2 p-3 flex items-center gap-3 bg-tb-cream"
        style={{ borderColor: color }}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
          style={{ background: tint, border: `2px solid ${color}` }}>
          <Icon className="w-6 h-6" strokeWidth={1.8} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif-it text-tb-navy text-lg leading-tight truncate">{a.name}</div>
          <div className="font-ui text-xs text-tb-navy/70 truncate">{a.description}</div>
        </div>
      </div>
    );
  }
  // Locked / in-progress
  const pct = Math.min(100, (a.progress / a.threshold) * 100);
  const isMoney = a.metric === "saved";
  const fmt = (n) => isMoney
    ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : String(n);
  return (
    <div data-testid={PASSPORT.achievement(a.id)}
      className="rounded-2xl border-2 border-dashed border-tb-cream-3 p-3 flex flex-col gap-1.5 bg-tb-cream-2/60">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-tb-cream-3/60 flex items-center justify-center shrink-0">
          <Icon className="w-6 h-6 text-tb-navy/40" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif-it text-tb-navy/80 text-lg leading-tight flex items-center gap-1">
            <Lock className="w-3 h-3" /> {a.name}
          </div>
          <div className="font-ui text-xs text-tb-navy/60 leading-snug">{a.description}</div>
        </div>
      </div>
      <div className="flex items-center justify-end mt-1">
        <span className="font-ui text-xs text-tb-navy/70">{fmt(a.progress)} / {fmt(a.threshold)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-tb-cream-3 overflow-hidden">
        <div className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

/* ---------- Rewards card (balance + tier progress) ---------- */
function RewardsCard({ r }) {
  return (
    <div data-testid={PASSPORT.rewardsCard}
      className="rounded-3xl border-2 border-tb-gold/60 p-5 text-tb-blue-soft"
      style={{
        background:
          "linear-gradient(135deg, #1a3a5f 0%, #2a4a72 55%, #1a3a5f 100%)",
      }}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-hand text-tb-blue text-base italic">your balance</div>
          <div data-testid={PASSPORT.rewardsBalance}
            className="font-serif text-white text-5xl leading-none mt-1">
            {r.balance.toLocaleString()}
          </div>
          <div className="font-hand text-tb-blue mt-1">points <span className="text-tb-gold">✨</span></div>
        </div>
        <div data-testid={PASSPORT.rewardsTier} className="text-right">
          <div className="font-hand text-tb-blue text-sm italic">current tier</div>
          <div className="flex items-center justify-end gap-1.5 mt-0.5">
            <span className="font-serif-it text-2xl" style={{ color: r.tier.color }}>
              {r.tier.name}
            </span>
            <span className="text-tb-gold-bright">✦</span>
          </div>
          <Award className="w-6 h-6 ml-auto mt-1 text-tb-blue/70" strokeWidth={1.5} />
        </div>
      </div>

      {r.next_tier && (
        <div className="mt-5">
          <div className="flex items-end justify-between text-xs font-ui mb-1.5">
            <span style={{ color: r.tier.color }}>{r.tier.name}</span>
            <span className="font-hand text-tb-blue/80 italic">
              {r.points_to_next} pts to {r.next_tier.name}
            </span>
            <span style={{ color: r.next_tier.color }}>{r.next_tier.name}</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${r.tier_progress_pct}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full rounded-full"
              style={{
                background: `linear-gradient(90deg, ${r.tier.color}, ${r.next_tier.color})`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function EarnList({ methods, onRefer }) {
  return (
    <div className="bg-tb-cream rounded-2xl border-2 border-tb-cream-3 px-4 py-2">
      {methods.map((m, i) => {
        const Icon = EARN_ICONS[m.icon] || Zap;
        const isRefer = m.id === "refer";
        return (
          <button
            key={m.id}
            data-testid={isRefer ? PASSPORT.referOpen : PASSPORT.earnMethod(m.id)}
            onClick={isRefer ? onRefer : undefined}
            disabled={!isRefer}
            className={
              "w-full flex items-center justify-between py-3 " +
              (i < methods.length - 1 ? "border-b border-dashed border-tb-cream-3 " : "") +
              (isRefer ? "tb-tap hover:bg-tb-gold/5 -mx-2 px-2 rounded-lg" : "cursor-default")
            }
          >
            <span className="flex items-center gap-3">
              <Icon className="w-5 h-5 text-tb-navy/70" strokeWidth={1.8} />
              <span className="font-ui text-tb-navy">{m.label}</span>
            </span>
            <span className="font-hand text-tb-coral font-bold text-lg">+{m.points} pts</span>
          </button>
        );
      })}
    </div>
  );
}

function RedeemCard({ rd, onRedeem }) {
  const claimed = rd.claimed;
  const locked = !rd.unlocked && !claimed;
  return (
    <div data-testid={PASSPORT.redeemCard(rd.id)}
      className={
        "rounded-2xl border-2 px-3 py-3 flex items-center gap-3 " +
        (claimed
          ? "bg-tb-blue-soft border-tb-blue"
          : rd.unlocked
            ? "bg-tb-cream border-tb-blue"
            : "bg-tb-cream-2/60 border-tb-cream-3 opacity-80")
      }>
      <div className={
        "w-11 h-11 rounded-full shrink-0 flex items-center justify-center " +
        (locked ? "bg-tb-cream-3" : "bg-tb-blue-soft")
      }>
        <Gift className={"w-5 h-5 " + (locked ? "text-tb-navy/40" : "text-tb-blue")} strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={"font-serif-it text-lg leading-tight " + (locked ? "text-tb-navy/60" : "text-tb-navy")}>
          {rd.label}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <Zap className={"w-3.5 h-3.5 " + (locked ? "text-tb-navy/40" : "text-tb-blue")} fill="currentColor" />
          <span className={"font-hand " + (locked ? "text-tb-navy/60" : "text-tb-navy")}>
            {rd.points.toLocaleString()} pts
          </span>
          {rd.tag && (
            <span className={
              "text-[10px] font-hand px-2 py-0.5 rounded-full " +
              (rd.tag === "Most Popular"
                ? "bg-tb-coral text-white"
                : "bg-tb-coral-soft text-tb-coral")
            }>{rd.tag}</span>
          )}
        </div>
      </div>
      <button
        data-testid={PASSPORT.redeemBtn(rd.id)}
        disabled={claimed}
        onClick={() => onRedeem(rd)}
        className={
          "px-4 py-1.5 rounded-full font-hand text-base tb-tap " +
          (claimed
            ? "bg-tb-blue text-white cursor-default"
            : rd.unlocked
              ? "bg-tb-coral text-white shadow-sm hover:brightness-110"
              : "bg-neutral-100 border border-neutral-300 text-tb-navy/60 hover:bg-neutral-200 font-ui italic")
        }>
        {claimed ? "Claimed ✓" : rd.unlocked ? "Redeem!" : "Locked"}
      </button>
    </div>
  );
}

function ReferDialog({ onClose, onSubmit }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rewards/referral-link");
        setLink(data);
      } catch (err) {
        console.error("[PassportPage] failed to fetch referral link:", err);
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(email); onClose(); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    const toCopy = link?.share_url || link?.url;
    if (!toCopy) return;
    try {
      await navigator.clipboard.writeText(toCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (err) {
      console.error("[PassportPage] clipboard copy failed:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3">
      <form onSubmit={submit}
        role="dialog" aria-modal="true" aria-labelledby="refer-dialog-title"
        className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full">
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span id="refer-dialog-title" className="font-hand text-xl">refer a friend ✨</span>
          <button type="button" onClick={onClose} className="font-hand text-tb-gold tb-tap">close</button>
        </div>
        <div className="p-4 space-y-4">
          <p className="font-hand text-tb-navy/70 italic">
            Earn <b className="text-tb-coral">+250 pts</b> for every traveler you bring on board.
          </p>

          {/* Personal link */}
          {link && (
            <div className="rounded-xl border-2 border-tb-cream-3 bg-tb-cream-2/50 p-3">
              <div className="font-hand text-tb-blue text-base italic mb-1">~ your invite link ~</div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="passport-refer-link"
                  className="font-mono text-xs text-tb-navy bg-tb-cream rounded-md border border-tb-cream-3 px-2 py-1.5 flex-1 truncate"
                  title={link.share_url || link.url}
                >
                  {link.share_url || link.url}
                </code>
                <button
                  type="button"
                  data-testid="passport-refer-copy"
                  onClick={copy}
                  className="shrink-0 px-3 py-1.5 rounded-md bg-tb-navy text-tb-gold-bright font-hand text-sm border-2 border-tb-gold tb-tap"
                >
                  {copied ? "Copied ✓" : "Copy"}
                </button>
              </div>
              <div className="font-hand text-tb-navy/60 text-xs mt-1.5">
                You've sent {link.referrals_count} invite{link.referrals_count === 1 ? "" : "s"} so far.
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="h-px flex-1 bg-tb-cream-3" />
            <span className="font-hand text-tb-navy/50 text-xs">or invite by email (instant credit)</span>
            <span className="h-px flex-1 bg-tb-cream-3" />
          </div>

          <input
            data-testid={PASSPORT.referEmail}
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="friend@email.com"
            className="w-full px-3 py-2 rounded-lg bg-tb-cream border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui" />
          <button
            data-testid={PASSPORT.referSubmit}
            disabled={busy} type="submit"
            className="w-full py-3 rounded-xl bg-tb-navy text-tb-gold-bright font-hand text-xl border-2 border-tb-gold tb-tap disabled:opacity-60">
            {busy ? "sending…" : "send invite & claim 250 pts"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- Page ---------- */
export default function PassportPage() {
  const [data, setData] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referring, setReferring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/passport"),
        api.get("/rewards"),
      ]);
      setData(p.data); setRewards(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [waitlistFor, setWaitlistFor] = useState(null);
  const onRedeem = (rd) => {
    // Redemptions are locked for now — open the wait-list dialog instead so
    // we can capture interest + contact info for the launch list.
    setWaitlistFor(rd);
  };

  const onRefer = async (email) => {
    try {
      const { data } = await api.post("/rewards/refer", { email });
      setRewards(data);
      toast.success("+150 pts — invite sent!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading || !data || !rewards) return (
    <div className="flex-1 flex items-center justify-center">
      <span className="font-hand text-tb-navy/60 text-xl">~ opening your passport ~</span>
    </div>
  );

  const stamps = data.stamps || [];
  const unlocked = (data.achievements || []).filter((a) => a.unlocked);
  const inProgress = (data.achievements || []).filter((a) => !a.unlocked);

  return (
    <div data-testid={PASSPORT.page} className="flex flex-col bg-white">
      <SectionRibbon>stamps, badges &amp; rewards</SectionRibbon>
      <span data-tour="passport-header" className="block" style={{position: "absolute", pointerEvents: "none", height: 1, width: 1}} aria-hidden="true" />

      <div className="px-5 py-6 space-y-8">
        {/* ---- Rewards Phase C · V2 tier progress bar ----
            Mounted at the very top of the passport page (above every
            other section) and REPLACES the legacy `RewardsCard` V1
            blue box that used to sit inside the rewards section
            below.  Uses the exact same blue-gradient / gold-border
            visual so travelers see continuity, but now driven by
            `/rewards/v2/tier-status` (lifetime_points + current_tier
            + progress_to_next). */}
        <TierProgressBar />

        {/* Rewards Phase D · read-only monetary reveal.
            Points + USD credit equivalent + helper.  NO CTA — Apply
            button only ships when TravelBio owns a checkout surface. */}
        <RewardsCreditBlock />

        {/* ---- Stamps ---- */}
        <section>
          <h2 className="text-center font-hand text-tb-coral text-2xl mb-4">
            ✦ stamps collected ✦
          </h2>
          <div className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream tb-paper py-6 px-3">
            <div className="grid grid-cols-3 gap-y-6 gap-x-2 place-items-center">
              {stamps.map((s, i) => <StampOval key={s.trip_id} stamp={s} idx={i} />)}
              <NextTripPlaceholder />
            </div>
            <p className="text-center font-hand text-tb-navy/80 mt-5 text-lg">
              {data.stats.stamps} stamps · {data.stats.countries} {data.stats.countries === 1 ? "country" : "countries"} explored ✈️
            </p>
          </div>
        </section>

        {/* ---- Achievements ---- */}
        <section>
          <h2 className="text-center font-hand text-tb-purple text-2xl mb-4 flex items-center justify-center gap-2">
            <span>✿</span> achievements <span>✿</span>
          </h2>
          <div data-testid={PASSPORT.achievementsList}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {unlocked.map((a) => <AchievementCard key={a.id} a={a} />)}
          </div>
          {inProgress.length > 0 && (
            <>
              <div className="flex items-center gap-2 mt-5 mb-3">
                <Lock className="w-4 h-4 text-tb-navy/70" />
                <span className="font-hand text-tb-navy/80 text-lg italic">in progress…</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {inProgress.map((a) => <AchievementCard key={a.id} a={a} />)}
              </div>
            </>
          )}
        </section>

        {/* ---- Rewards ---- */}
        <section>
          <h2 className="text-center font-hand text-tb-navy text-2xl mb-4 flex items-center justify-center gap-2">
            <Binoculars className="w-5 h-5 text-tb-blue" /> rewards <Binoculars className="w-5 h-5 text-tb-blue" />
          </h2>

          {/* 2026-08-12 · Phase C — V1 `RewardsCard` (blue "Bronze"
              box) removed from this position.  The V2
              `TierProgressBar` at the top of the page now owns the
              tier + progress display.  This section keeps the
              `redeem` list only; full rewards dashboard is Phase E. */}

          <h3 className="flex items-center gap-2 mt-1 mb-2">
            <Gift className="w-5 h-5 text-tb-orange" />
            <span className="font-hand text-tb-orange text-lg">redeem</span>
          </h3>
          <div className="space-y-2.5">
            {rewards.redemptions.map((rd) => (
              <RedeemCard key={rd.id} rd={rd} onRedeem={onRedeem} />
            ))}
          </div>

          {rewards.redemptions_claimed?.length > 0 && (
            <p className="font-hand text-tb-navy/60 italic text-sm mt-3 text-center">
              you&apos;ve claimed {rewards.redemptions_claimed.length} reward
              {rewards.redemptions_claimed.length === 1 ? "" : "s"} so far ✨
            </p>
          )}
        </section>

        <p className="text-center font-hand text-tb-navy/50 italic text-sm">
          add a chapter in the <b>Chapters</b> tab to earn a new stamp ✦
        </p>
      </div>

      {referring && <ReferDialog onClose={() => setReferring(false)} onSubmit={onRefer} />}
      {waitlistFor && (
        <WaitlistDialog
          rd={waitlistFor}
          onClose={() => setWaitlistFor(null)}
        />
      )}
    </div>
  );
}

function WaitlistDialog({ rd, onClose }) {
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post("/rewards/waitlist", {
        reward_id: rd.id,
        contact: contact.trim(),
      });
      toast.success("On the list ✨ we'll ping you when redemptions open");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3"
      data-testid="waitlist-dialog"
      role="dialog"
      aria-modal="true"
    >
      <form onSubmit={submit} className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full">
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-xl">redemptions coming soon</span>
          <button type="button" onClick={onClose} className="font-hand text-tb-gold tb-tap">close</button>
        </div>
        <div className="p-4 space-y-3">
          <p className="font-serif-it text-tb-navy text-lg leading-snug">
            We&apos;re polishing <b>{rd.label}</b> with our travel partners.
          </p>
          <p className="font-hand text-tb-navy/70 text-sm italic">
            Want a heads-up the moment it opens? Drop your email or phone (totally optional).
          </p>
          <input
            data-testid="waitlist-contact-input"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="email or phone (optional)"
            className="w-full px-3 py-2 rounded-lg bg-tb-cream border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-tb-cream-2 border-2 border-tb-cream-3 font-hand text-tb-navy/70 tb-tap"
              data-testid="waitlist-skip-btn"
            >
              skip
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap disabled:opacity-60"
              data-testid="waitlist-submit-btn"
            >
              {busy ? "saving…" : "Notify me"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
